/**
 * Catálogo AR del GeoSim toolbar — mismos nombres exactos que /admin/cta-por-region.
 *
 * Fuentes:
 *   - provincias: AR_PROVINCES (geoTrafficCatalog)
 *   - ciudades: AR_TOP_CITIES + /api/geo-cities-ar-public (enabled)
 *   - barrios: buildNeighborhoodSeedCatalog (CABA GCBA + GBA partidos)
 */

import {
  AR_PROVINCES,
  AR_TOP_CITIES,
} from "@/config/geoTrafficCatalog";
import {
  buildNeighborhoodSeedCatalog,
  normGeoKey,
} from "@/../api/lib/neighborhoodBetaCatalog";

export type ArGeoProvince = { code: string; name: string; region: string };
export type ArGeoCity = { name: string; province: string };
export type ArGeoNeighborhood = { city: string; neighborhood: string };

export const GEO_SIM_AR_PROVINCES_FULL: ArGeoProvince[] = AR_PROVINCES.map((p) => ({
  code: p.code,
  name: p.name,
  region: p.region,
}));

/** Fallback estático (idéntico a cta-por-region si falla el API de ciudades). */
export const GEO_SIM_AR_CITIES_STATIC: ArGeoCity[] = AR_TOP_CITIES.map((c) => ({
  name: c.name,
  province: c.province,
}));

/** Pares ciudad+barrio exactos del catálogo beta (admin NeighborhoodCtaPanel). */
export const GEO_SIM_AR_NEIGHBORHOODS: ArGeoNeighborhood[] =
  buildNeighborhoodSeedCatalog().map((t) => ({
    city: t.city,
    neighborhood: t.neighborhood,
  }));

/**
 * Presets rápidos — solo nombres que existen en AR_PROVINCES / AR_TOP_CITIES /
 * buildNeighborhoodSeedCatalog (misma verdad que /admin/cta-por-region).
 */
export const GEO_SIM_AR_QUICK_LOCATIONS: Array<{
  label: string;
  province: string;
  city: string;
  neighborhood?: string;
}> = [
  { label: "CABA · capital", province: "CABA", city: "Buenos Aires" },
  {
    label: "CABA · Palermo",
    province: "CABA",
    city: "Buenos Aires",
    neighborhood: "Palermo",
  },
  {
    label: "CABA · Barracas",
    province: "CABA",
    city: "Buenos Aires",
    neighborhood: "Barracas",
  },
  {
    label: "CABA · Monserrat",
    province: "CABA",
    city: "Buenos Aires",
    neighborhood: "Monserrat",
  },
  {
    label: "CABA · Recoleta",
    province: "CABA",
    city: "Buenos Aires",
    neighborhood: "Recoleta",
  },
  {
    label: "CABA · Puerto Madero",
    province: "CABA",
    city: "Buenos Aires",
    neighborhood: "Puerto Madero",
  },
  { label: "GBA · Tigre", province: "Buenos Aires", city: "Tigre" },
  {
    label: "GBA · Nordelta",
    province: "Buenos Aires",
    city: "Tigre",
    neighborhood: "Nordelta",
  },
  { label: "GBA · San Isidro", province: "Buenos Aires", city: "San Isidro" },
  {
    label: "GBA · Vicente López",
    province: "Buenos Aires",
    city: "Vicente López",
  },
  { label: "GBA · La Plata", province: "Buenos Aires", city: "La Plata" },
  { label: "Córdoba capital", province: "Córdoba", city: "Córdoba" },
  { label: "Córdoba · Carlos Paz", province: "Córdoba", city: "Carlos Paz" },
  { label: "Rosario", province: "Santa Fe", city: "Rosario" },
  { label: "Santa Fe capital", province: "Santa Fe", city: "Santa Fe" },
  { label: "Mendoza capital", province: "Mendoza", city: "Mendoza" },
  {
    label: "Tucumán capital",
    province: "Tucumán",
    city: "San Miguel de Tucumán",
  },
  {
    label: "Bariloche",
    province: "Río Negro",
    city: "San Carlos de Bariloche",
  },
  { label: "Neuquén capital", province: "Neuquén", city: "Neuquén" },
];

export function mergeArCitiesWithDynamic(
  dynamic: ReadonlyArray<{ name: string; province_code: string }> | null | undefined,
): ArGeoCity[] {
  const byNorm = new Map<string, ArGeoCity>();
  for (const c of GEO_SIM_AR_CITIES_STATIC) {
    byNorm.set(normGeoKey(c.name), c);
  }
  if (Array.isArray(dynamic)) {
    for (const row of dynamic) {
      const name = String(row.name || "").trim();
      const province = String(row.province_code || "").trim();
      if (!name || !province) continue;
      const key = normGeoKey(name);
      // Prefer DB name/province when present (canonical for that brand).
      byNorm.set(key, { name, province });
    }
  }
  return Array.from(byNorm.values()).sort((a, b) => {
    const pc = a.province.localeCompare(b.province, "es");
    if (pc !== 0) return pc;
    return a.name.localeCompare(b.name, "es");
  });
}

export function filterArCitiesByProvince(
  cities: ReadonlyArray<ArGeoCity>,
  provinceCode: string,
): ArGeoCity[] {
  if (!provinceCode) return [...cities];
  const p = normGeoKey(provinceCode);
  return cities.filter((c) => normGeoKey(c.province) === p);
}

export function filterArNeighborhoodsByCity(
  neighborhoods: ReadonlyArray<ArGeoNeighborhood>,
  cityName: string,
): ArGeoNeighborhood[] {
  if (!cityName) return [];
  const c = normGeoKey(cityName);
  return neighborhoods
    .filter((n) => normGeoKey(n.city) === c)
    .sort((a, b) => a.neighborhood.localeCompare(b.neighborhood, "es"));
}

/** Infer province for a city (static map + optional dynamic list). */
export function provinceForArCity(
  cityName: string,
  cities: ReadonlyArray<ArGeoCity> = GEO_SIM_AR_CITIES_STATIC,
): string | null {
  const key = normGeoKey(cityName);
  const hit = cities.find((c) => normGeoKey(c.name) === key);
  return hit?.province ?? null;
}

/**
 * Fetch dynamic AR cities (same endpoint as WishlistAdmin / cta-por-region).
 * Returns null on failure so callers keep the static catalog.
 */
export async function fetchArCitiesPublic(): Promise<
  Array<{ name: string; province_code: string }> | null
> {
  try {
    const res = await fetch("/api/geo-cities-ar-public", {
      credentials: "same-origin",
      cache: "no-store",
    });
    if (!res.ok) return null;
    const data = (await res.json()) as {
      cities?: Array<{ name: string; province_code: string }>;
    };
    return Array.isArray(data.cities) ? data.cities : null;
  } catch {
    return null;
  }
}
